import React, { useEffect, useState } from 'react';

import { SafetyCertificateOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tag,
  message,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { ItemInterface } from 'models/ItemInterface';
import { Role } from 'models/Role';
import { User } from 'models/User';

import { UserService } from 'services/UserService';

import appI18n from '../../../../config/i18n';

interface DetailUserModalProps {
  isOpen: boolean;
  id: number;
  handleModalCancel: () => void;
  submit: (
    id: number,
    name: string,
    lastname: string,
    email: string,
    dialCode: string | null,
    phone: string | null,
    roleIds: number[]
  ) => void;
  roles: Role[];
  defaultCountryCode: string;
}

function DetailUserModal({
  isOpen,
  handleModalCancel,
  submit,
  roles,
  defaultCountryCode,
  id,
}: DetailUserModalProps) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState<
    'SUPER_ADMIN' | 'ADMIN' | 'REGULAR_ACCOUNT'
  >('REGULAR_ACCOUNT');
  const { t } = useTranslation(undefined, { i18n: appI18n });

  useEffect(() => {
    form.resetFields();
    form.setFieldValue('phone', {
      ...form.getFieldValue('phone'),
      countryCode: parseInt(defaultCountryCode),
    });
    setUserType('REGULAR_ACCOUNT');

    if (isOpen) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);

    const response = await UserService.findById(id);

    if (response.success) {
      const data = response.data as User;
      form.setFieldValue('name', data.name);
      form.setFieldValue('lastname', data.lastname);
      form.setFieldValue('email', data.email);
      form.setFieldValue(
        'roleIds',
        data.roles.map((record) => {
          return record.id;
        })
      );

      if (data.phone && data.dialCode) {
        const newPhone = {
          ...form.getFieldValue('phone'),
          countryCode: parseInt(data.dialCode),
          areaCode: null,
          phoneNumber: null,
        };

        if (data.phone) {
          newPhone.areaCode = data.phone;
        }

        form.setFieldValue('phone', newPhone);
      }

      setUserType(data.userType);

      setLoading(false);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.settings.users.components.detail_user_modal.errors.fetch_user_failed'
          ),
        3.5
      );
      handleModalCancel();
    }

    setLoading(false);
  };

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit(
      id,
      values.name,
      values.lastname,
      values.email,
      values.phone.phoneNumber === null ? null : `+${values.phone.countryCode}`,
      values.phone.phoneNumber === null
        ? null
        : `${values.phone.areaCode}${values.phone.phoneNumber}`,
      values.roleIds || []
    );

    setLoading(false);
  };

  const getRoles = (): ItemInterface[] => {
    return roles.map((record) => {
      return {
        value: record.id,
        label: record.name,
      };
    });
  };

  return (
    <>
      {contextHolder}

      <Modal
        title={
          <span>
            {t('scenes.settings.users.components.detail_user_modal.title', {
              id,
            })}
            {userType === 'SUPER_ADMIN' && (
              <Tag color="processing" style={{ marginLeft: '10px' }}>
                <SafetyCertificateOutlined />{' '}
                {t(
                  'scenes.settings.users.components.detail_user_modal.super_admin_badge'
                )}
              </Tag>
            )}
            {userType === 'ADMIN' && (
              <Tag color="purple" style={{ marginLeft: '10px' }}>
                <SafetyCertificateOutlined />{' '}
                {t(
                  'scenes.settings.users.components.detail_user_modal.admin_badge'
                )}
              </Tag>
            )}
          </span>
        }
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={480}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="email"
                label={t(
                  'scenes.settings.users.components.detail_user_modal.labels.email'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.users.components.detail_user_modal.validations.email.required'
                    ),
                  },
                  {
                    type: 'email',
                    message: t(
                      'scenes.settings.users.components.detail_user_modal.validations.email.invalid'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.detail_user_modal.placeholders.email'
                  )}
                  addonAfter={'@'}
                  readOnly={userType === 'SUPER_ADMIN' || userType === 'ADMIN'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="name"
                label={t(
                  'scenes.settings.users.components.detail_user_modal.labels.name'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.users.components.detail_user_modal.validations.name.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.detail_user_modal.placeholders.name'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="lastname"
                label={t(
                  'scenes.settings.users.components.detail_user_modal.labels.lastname'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.users.components.detail_user_modal.validations.lastname.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.detail_user_modal.placeholders.lastname'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="phone"
                label={t(
                  'scenes.settings.users.components.detail_user_modal.labels.phone'
                )}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      if (value.valid()) {
                        return Promise.resolve();
                      }

                      if (!value.valid() && value.phoneNumber === null) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.users.components.detail_user_modal.validations.phone.invalid'
                          )
                        )
                      );
                    },
                  },
                ]}
              >
                <PhoneInput
                  enableSearch
                  searchNotFound={t(
                    'scenes.settings.users.components.detail_user_modal.phone.search_not_found'
                  )}
                  searchPlaceholder={t(
                    'scenes.settings.users.components.detail_user_modal.phone.search_placeholder'
                  )}
                  allowClear
                  placeholder={t(
                    'scenes.settings.users.components.detail_user_modal.placeholders.phone'
                  )}
                  disabled={loading}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="roleIds"
                label={t(
                  'scenes.settings.users.components.detail_user_modal.labels.roles'
                )}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  options={getRoles()}
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.detail_user_modal.placeholders.roles'
                  )}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.users.components.detail_user_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default DetailUserModal;
